import React from "react";

import { gql } from "@apollo/client";
import { BILLING_DETAILS_QUERY } from "@features/billing";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type AddNewStripePaymentMethod,
  type AddNewStripePaymentMethodVariables,
  type UpsertBrandBillingInfo as UpsertBrandBillingInfoQuery,
  type UpsertBrandBillingInfoVariables,
} from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";
import { TextInput } from "@shared/text-input";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { type StripeCardElementOptions } from "@stripe/stripe-js";

const UPSERT_BILLING_INFO = gql`
  mutation UpsertBrandBillingInfo(
    $billingName: String!
    $billingEmail: String!
  ) {
    upsertBrandBillingInfo(
      billingName: $billingName
      billingEmail: $billingEmail
    )
  }
`;

const ADD_NEW_STRIPE_PAYMENT_METHOD = gql`
  mutation AddNewStripePaymentMethod($paymentMethodId: ID!) {
    addNewStripePaymentMethod(paymentMethodId: $paymentMethodId)
  }
`;

export interface AddPaymentMethodFormProps {
  readonly onSuccess?: () => void;
}

export const AddPaymentMethodForm = ({
  onSuccess,
}: AddPaymentMethodFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = React.useState("");
  const [stripeLoading, setStripeLoading] = React.useState(false);
  const [billingName, setBillingName] = React.useState("");
  const [billingEmail, setBillingEmail] = React.useState("");

  const [
    addNewStripePaymentMethod,
    { loading: addNewStripePaymentMethodLoading },
  ] = useNorthbeamMutation<
    AddNewStripePaymentMethod,
    AddNewStripePaymentMethodVariables
  >(ADD_NEW_STRIPE_PAYMENT_METHOD);

  const [upsertBillingInfo, { loading: upsertBillingInfoLoading }] =
    useNorthbeamMutation<
      UpsertBrandBillingInfoQuery,
      UpsertBrandBillingInfoVariables
    >(UPSERT_BILLING_INFO, {
      refetchQueries: [{ query: BILLING_DETAILS_QUERY }],
    });

  const cardOptions: StripeCardElementOptions = React.useMemo(
    () => ({
      iconStyle: "solid",
      style: {
        base: {
          iconColor: "#08212b",
          color: "#212529",
          fontWeight: "500",
          fontFamily:
            "Commons Pro, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
          fontSize: "16px",
          fontSmoothing: "antialiased",
          ":-webkit-autofill": {
            color: "#fce883",
          },
          "::placeholder": {
            color: "#6c757d",
          },
        },
        invalid: {
          iconColor: "#dc3545",
          color: "#dc3545",
        },
      },
    }),
    [],
  );

  const handleSubmit = React.useCallback(
    async (event) => {
      // Block native form submission.
      event.preventDefault();

      if (!stripe || !elements) {
        /* Stripe.js has not loaded yet. Make sure to disable form submission until Stripe.js has
           loaded. */
        return;
      }

      /* Get a reference to a mounted CardElement. Elements knows how to find your CardElement
         because there can only ever be one of each type of element. */
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        return;
      }

      setStripeLoading(true);
      // Use your card Element with other Stripe.js APIs
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });
      await upsertBillingInfo({
        variables: {
          billingName: billingName,
          billingEmail: billingEmail,
        },
      });
      setStripeLoading(false);

      if (error) {
        setErrorMessage(error.message || "Unknown Stripe error.");
      } else {
        const result = await addNewStripePaymentMethod({
          variables: { paymentMethodId: paymentMethod!.id },
        });

        if (result.errors) {
          setErrorMessage("Could not attach payment method to user.");
        } else {
          onSuccess && onSuccess();
        }
      }
    },
    [
      stripe,
      elements,
      addNewStripePaymentMethod,
      setStripeLoading,
      billingName,
      billingEmail,
      upsertBillingInfo,
      setErrorMessage,
      onSuccess,
    ],
  );

  const loading =
    stripeLoading ||
    addNewStripePaymentMethodLoading ||
    upsertBillingInfoLoading;

  return (
    <form className="flex flex-col space-y-4" onSubmit={handleSubmit}>
      <TextInput
        label="Billing name"
        placeholder="Jane Doe"
        isRequired
        autoComplete="name"
        value={billingName}
        onChange={({ target }) => setBillingName(target.value)}
      />
      <TextInput
        label="Billing email"
        type="email"
        placeholder="jane.doe@gmail.com"
        isRequired
        autoComplete="email"
        value={billingEmail}
        onChange={({ target }) => setBillingEmail(target.value)}
      />
      <div className="nb-input-container">
        <label className="label">Credit card</label>
        <CardElement className="nb-input" options={cardOptions} />
        {errorMessage && (
          <div className="error alert alert-danger">{errorMessage}</div>
        )}
      </div>
      <div>
        <PrimaryButton
          type="submit"
          size="md"
          loading={loading}
          disabled={!billingName || !billingEmail}
        >
          ADD CREDIT CARD
        </PrimaryButton>
      </div>
    </form>
  );
};
